import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';
import { Search, Loader2, UserPlus, X, Users } from 'lucide-react';
import { searchUsers, addMemberToGroup } from '../../../redux/userGroups/groups/userGroupies';
import { clearSearchResults } from '../../../redux/userGroups/groups/groupReducer';

/**
 * Replaces the orphaned GroupUserSearchComp.jsx (never rendered, 2 broken
 * imports, read from a Redux key that was never registered). Trimmed to
 * just the "invite new people" mode -- a modal's job here is inviting, not
 * also browsing the existing roster.
 */
const InviteMembersModal = ({ isOpen, onClose, groupId, existingMembers = [], onInvited }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [query, setQuery] = useState('');
  const [debouncedQuery] = useDebounce(query, 400);
  const [invitingUserId, setInvitingUserId] = useState(null);
  const [inviteError, setInviteError] = useState(null);

  const { searchResults, searchStatus, searchError } = useSelector((state) => state.groups);

  const existingMemberIds = useMemo(
    () => new Set(existingMembers.map((m) => m.user_id ?? m.id)),
    [existingMembers]
  );

  const normalized = useMemo(() => debouncedQuery.trim().replace(/^@/, ''), [debouncedQuery]);

  useEffect(() => {
    if (!isOpen) return;
    if (normalized) {
      dispatch(searchUsers({ query: normalized, skip: 0, limit: 20 }));
    } else {
      dispatch(clearSearchResults());
    }
  }, [normalized, isOpen, dispatch]);

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      dispatch(clearSearchResults());
    }
  }, [isOpen, dispatch]);

  if (!isOpen) return null;

  const invitableResults = (searchResults || []).filter(
    (u) => !existingMemberIds.has(u.id ?? u.user_id)
  );

  const handleInvite = async (userId) => {
    setInvitingUserId(userId);
    setInviteError(null);
    try {
      await dispatch(addMemberToGroup({ groupId, userId })).unwrap();
      onInvited?.();
    } catch (err) {
      setInviteError(typeof err === 'string' ? err : t('groups.inviteModal.failedToInvite'));
    } finally {
      setInvitingUserId(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-indigo-500/30 max-w-md w-full max-h-[80vh] flex flex-col">
        <div className="p-6 flex-1 flex flex-col min-h-0">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
              <UserPlus size={20} className="text-indigo-400" />
              <span>{t('groups.inviteModal.heading')}</span>
            </h2>
            <button onClick={onClose} className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
              <X size={18} />
            </button>
          </div>

          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600 dark:text-gray-400 h-5 w-5" />
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('groups.inviteModal.searchPlaceholder')}
              className="w-full p-3 pl-10 pr-4 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-700 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
            />
          </div>

          {inviteError && (
            <div className="mb-3 text-sm text-red-700 dark:text-red-400 bg-red-100 dark:bg-red-500/10 border border-red-500/20 rounded-lg p-2">
              {inviteError}
            </div>
          )}

          <div className="flex-1 overflow-y-auto space-y-2 min-h-[120px]">
            {searchStatus === 'loading' && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 text-indigo-500 animate-spin" />
              </div>
            )}

            {searchError && (
              <div className="text-red-700 dark:text-red-400 text-sm p-3 bg-red-100 dark:bg-red-500/10 rounded-lg">{searchError}</div>
            )}

            {searchStatus !== 'loading' && !normalized && (
              <div className="text-center py-10 text-gray-500 dark:text-gray-500 text-sm">
                <Users className="mx-auto mb-2 h-8 w-8 text-gray-400 dark:text-gray-600" />
                {t('groups.inviteModal.typeToFindPeople')}
              </div>
            )}

            {searchStatus === 'succeeded' && normalized && invitableResults.length === 0 && (
              <div className="text-center py-10 text-gray-500 dark:text-gray-500 text-sm">
                {t('groups.inviteModal.noMatchingUsers')}
              </div>
            )}

            {invitableResults.map((user) => {
              const uid = user.id ?? user.user_id;
              const displayName = user.username || user.email || t('groups.inviteModal.userFallback', { id: uid });
              return (
                <div
                  key={uid}
                  className="flex items-center justify-between p-3 bg-cream-100 dark:bg-gray-900/60 rounded-lg border border-cream-300 dark:border-gray-700/50"
                >
                  <div className="min-w-0">
                    <p className="text-gray-900 dark:text-white font-medium truncate">@{displayName}</p>
                    {user.email && <p className="text-gray-500 dark:text-gray-500 text-xs truncate">{user.email}</p>}
                  </div>
                  <button
                    onClick={() => handleInvite(uid)}
                    disabled={invitingUserId === uid}
                    data-testid={`invite-user-${displayName}`}
                    className="px-3 py-1.5 rounded-lg text-sm flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white transition-colors"
                  >
                    {invitingUserId === uid ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <UserPlus className="h-4 w-4" />
                    )}
                    <span>{t('groups.inviteModal.invite')}</span>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InviteMembersModal;
